import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import {
  useConfirmInvites,
  useDeleteMember,
  usePendingInvite,
  useResendInvite,
  useScopeListing,
  useUpdateUserDetails,
  useUserInvite,
} from '../../hooks/Admin';
import { useCurrentUserProfile } from '../../hooks/user';
import useGlobalPermissions from '../../useGlobalPermissions';
import GlobalHelper from '../../utils/globalHelper';
import fetchRoles from '../../api/roles';
import { useModuleAccess } from '../../hooks/useConstant';
import routes from '../../routes';

function useSettings() {
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [checkScope, setCheckScope] = useState(false);
  const { data: ScopeList } = useScopeListing();
  const [entity, setEntity] = useState([]);
  const [entityOpen, setEntityOpen] = useState(false);
  const [isSelectAllEntity, setIsSelectAllEntity] = useState(false);
  const { moduleList } = useModuleAccess();

  const entityList = ScopeList?.Other?.EntityList;

  const handleEntityChange = (e) => {
    const { targetValue, checked } = e.target;
    if (checked) {
      setEntity([...entity, targetValue]);
    } else {
      const res = entity.filter((ent) => ent !== targetValue);
      setEntity(res);
    }
  };

  const selectAllEntity = (e) => {
    const { checked } = e.target;
    setIsSelectAllEntity(!isSelectAllEntity);
    if (checked) {
      setEntity(entityList?.map((item) => item.entity_name));
    } else {
      setEntity([]);
    }
  };

  useEffect(() => {
    if (entity?.length === entityList?.length) {
      setIsSelectAllEntity(true);
    } else {
      setIsSelectAllEntity(false);
    }
  }, [entity]);

  const [peopleScopeOpen, setPeopleScopeOpen] = useState(false);
  const [isSelectAllDepartment, setIsSelectAllDepartment] = useState(false);
  const [department, setDepartment] = useState([]);

  const departmentList = ScopeList?.Other?.DepartmentList;

  const handleDepartmentChange = (e) => {
    const { targetValue, checked } = e.target;
    if (checked) {
      setDepartment([...department, targetValue]);
    } else {
      const res = department.filter((ent) => ent !== targetValue);
      setDepartment(res);
    }
  };

  const selectAllDepartment = (e) => {
    const { checked } = e.target;
    setIsSelectAllDepartment(!isSelectAllDepartment);
    if (checked) {
      setDepartment(departmentList?.map((item) => item.department_name));
    } else {
      setDepartment([]);
    }
  };

  useEffect(() => {
    if (department?.length === departmentList?.length) {
      setIsSelectAllDepartment(true);
    } else {
      setIsSelectAllDepartment(false);
    }
  }, [department]);

  const [moduleAccessOpen, setModuleAccessOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [isSelectAllModules, setIsSelectAllModules] = useState(false);

  const handleModuleChange = (e) => {
    const { targetValue, checked } = e.target;
    if (checked) {
      setModules([...modules, targetValue]);
    } else {
      const res = modules.filter((ent) => ent !== targetValue);
      setModules(res);
    }
  };

  const selectAllModules = (e) => {
    const { checked } = e.target;
    setIsSelectAllModules(!isSelectAllModules);
    if (checked) {
      setModules(moduleList?.map((item) => item));
    } else {
      setModules([]);
    }
  };

  useEffect(() => {
    if (modules?.length === moduleList?.length) {
      setIsSelectAllModules(true);
    } else {
      setIsSelectAllModules(false);
    }
  }, [modules]);

  const [DataAccessOpen, setDataAccessOpen] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [isSelectAllMetrics, setIsSelectAllMetrics] = useState(false);

  const metricsList = ScopeList?.Metrics ? Object.keys(ScopeList?.Metrics) : [];
  const regexPatternForText = /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s-]*)$/gi;
  const handleMetricsChange = (e) => {
    const { targetValue, checked } = e.target;
    if (checked) {
      setMetrics([...metrics, targetValue]);
    } else {
      const res = metrics.filter((ent) => ent !== targetValue);
      setMetrics(res);
    }
  };

  const selectAllMetrics = (e) => {
    const { checked } = e.target;
    setIsSelectAllMetrics(!isSelectAllMetrics);
    if (checked) {
      setMetrics(metricsList?.map((item) => item));
    } else {
      setMetrics([]);
    }
  };

  useEffect(() => {
    if (metrics?.length === metricsList?.length) {
      setIsSelectAllMetrics(true);
    } else {
      setIsSelectAllMetrics(false);
    }
  }, [metrics]);

  const [role, setRole] = useState('user');
  const [roleOpen, setRoleOpen] = useState(false);

  const handleClickAway = (val) => {
    if (val === 'entity') {
      setEntityOpen(false);
    } else if (val === 'peopleScope') {
      setPeopleScopeOpen(false);
    } else if (val === 'dataAccess') {
      setDataAccessOpen(false);
    } else if (val === 'moduleAccess') {
      setModuleAccessOpen(false);
    } else if (val === 'role') {
      setRoleOpen(false);
    }
  };

  const [showInviteModal, setShowInviteModal] = useState(false);
  const { mutate: userInvite } = useUserInvite();
  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    type: 'user',
    job_title: '',
  };
  const signUpSchema = Yup.object({
    firstName: Yup.string()
      .matches(regexPatternForText, 'Name can only contain letters.')
      .strict(true)
      .min(2, 'First name must be at least 2 characters.')
      .max(25, 'First name must be at most 25 characters.')
      .required('Please enter your first name.'),
    lastName: Yup.string()
      .strict(true)
      .matches(regexPatternForText, 'Name can only contain letters.')
      .min(2, 'Last name must be at least 2 characters.')
      .max(25, 'Last name must be at most 25 characters.')
      .required('Please enter your last name.'),
    email: Yup.string()
      .trim('The email cannot include spaces.')
      .strict(true)
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        'Email must be a valid email.'
      )
      .ensure()
      .email('Email must be a valid email.')
      .required('Please enter your email.'),
    type: Yup.string().required('Please enter user role.'),
    job_title: Yup.string()
      .strict(true)
      .min(2, 'Job title must be at least 2 characters.')
      .max(25, 'Job title must be at most 25 characters.')
      .required('Please enter your job title.'),
  });

  const { data: confirmUserData } = useConfirmInvites();

  const [DataForEdit, setDataForEdit] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const { mutate: userUpdate } = useUpdateUserDetails();

  const handleEditModel = (data) => {
    setShowEditModal(true);
    setDataForEdit(data);
  };

  const { data: pendingUserData } = usePendingInvite();
  const { mutate: ResendInvite } = useResendInvite();
  const [deleteId, setDeleteId] = useState('');
  const { mutate: deleteMember } = useDeleteMember();
  const { userData } = useGlobalPermissions();
  const userType = userData?.data?.type;
  const handleResendInvite = (id) => {
    ResendInvite(id);
  };
  const handleChange = (_event, newValue) => {
    setValue(newValue);
  };
  const handleDeleteConfirm = (id) => {
    setDeleteId(id);
    setShowModal(true);
  };
  const navigate = useNavigate();
  const handleDeleteMember = () => {
    if (userData?.data?.id === deleteId) {
      deleteMember(deleteId);
      GlobalHelper.clearStorageWithoutLanguage();
      navigate(routes.HomePage, { replace: true });
    } else {
      deleteMember(deleteId);
    }
    setShowModal(false);
  };

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const { data: currentProfile } = useCurrentUserProfile();

  const accountSchema = Yup.object({
    firstName: Yup.string()
      .matches(regexPatternForText, 'First name can only contain letters.')
      .strict(true)
      .min(2, 'First name must be at least 2 characters.')
      .max(25, 'First name must be at most 25 characters.')
      .required('Please enter your first name.'),

    lastName: Yup.string()
      .matches(regexPatternForText, 'Last name can only contain letters.')
      .strict(true)
      .min(2, 'Last name must be at least 2 characters.')
      .max(25, 'Last name must be at most 25 characters.')
      .required('Please enter your last name.'),

    email: Yup.string()
      .trim('The email cannot include spaces.')
      .strict(true)
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        'Email must be a valid email.'
      )
      .ensure()
      .email('Email must be a valid email.')
      .required('Please enter your email.'),

    type: Yup.string().required('Please enter user role.'),

    job_title: Yup.string()
      .strict(true)
      .min(2, 'Job title must be at least 2 characters.')
      .max(25, 'Job title must be at most 25 characters.')
      .required('Please enter your job title.'),
  });

  const accountValues = {
    firstName: `${currentProfile?.data?.first_name}`,
    lastName: `${currentProfile?.data?.last_name}`,
    email: `${currentProfile?.data?.personal_email}`,
    type: 'user',
    password: `${currentProfile?.data?.password}`,
  };

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }
  const { data: roles } = useQuery('roles', () => fetchRoles());

  return {
    a11yProps,
    handleChange,
    setValue,
    value,
    t,
    userType,
    showInviteModal,
    setShowInviteModal,
    checkScope,
    setCheckScope,
    initialValues,
    signUpSchema,
    userInvite,
    confirmUserData,
    showEditModal,
    setShowEditModal,
    userUpdate,
    DataForEdit,
    handleEditModel,
    pendingUserData,
    handleResendInvite,
    handleDeleteConfirm,
    deleteMember,
    handleDeleteMember,
    showChangePasswordModal,
    setShowChangePasswordModal,
    currentProfile,
    accountSchema,
    accountValues,
    showModal,
    setShowModal,
    ScopeList,
    handleEntityChange,
    selectAllEntity,
    isSelectAllEntity,
    entityList,
    entity,
    setEntity,
    setEntityOpen,
    entityOpen,
    handleClickAway,
    peopleScopeOpen,
    setPeopleScopeOpen,
    department,
    setDepartment,
    isSelectAllDepartment,
    setIsSelectAllDepartment,
    selectAllDepartment,
    departmentList,
    handleDepartmentChange,
    DataAccessOpen,
    metrics,
    setMetrics,
    setDataAccessOpen,
    selectAllMetrics,
    isSelectAllMetrics,
    metricsList,
    handleMetricsChange,
    moduleAccessOpen,
    setModuleAccessOpen,
    modules,
    setModules,
    moduleList,
    handleModuleChange,
    isSelectAllModules,
    selectAllModules,
    roleOpen,
    setRoleOpen,
    role,
    setRole,
    userData,
    roles,
  };
}

export default useSettings;
